import { Fragment, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronUp, Info, Link2, Pencil, Plus, Trash2 } from 'lucide-react';
import { Card } from '../../../components/Shared/Card';
import { Button } from '../../../components/Shared/Button';
import { Badge } from '../../../components/Shared/Badge';
import { EmptyState } from '../../../components/Shared/EmptyState';
import {
  Table, TableBody, TableCell, TableHead,
  TableHeader, TableRow,
} from '../../../components/Shared/Table';
import { formatMoney } from '../../../utils/money';
import { numberLocale } from '../../../utils/locale';
import { BillingKinds, billingKindLabelKey, formatBillingRule, needsReview } from '../../../utils/billingKind';
import type { FacilityFlowCoreModulesOffersDtosOfferPositionDto as PositionDto } from '../../../api/generated/models';
import { NoTaskHint } from './NoTaskHint';

interface PositionsTableProps {
  positions: PositionDto[];
  /** Positionen sind editierbar — Entwurf oder angenommen ohne Rechnung. */
  isEditable: boolean;
  /** Positionen entstehen aus Aufgaben des Objekts — ohne Objekt kein Hinzufügen. */
  hasObject: boolean;
  onAdd: () => void;
  onEdit: (p: PositionDto) => void;
  onDelete: (p: PositionDto) => void;
  onMove: (index: number, direction: 'up' | 'down') => void;
  t: (key: string, opts?: Record<string, unknown>) => string;
}

export function PositionsSection({
  positions, isEditable, hasObject, onAdd, onEdit, onDelete, onMove, t,
}: PositionsTableProps) {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(
    new Set()
  );

  const toggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const colCount = isEditable ? 7 : 6;
  const reviewCount = positions.filter((p) => needsReview(p.recurrenceKind)).length;
  const ruleText = (pos: PositionDto) =>
    formatBillingRule(pos.recurrenceKind, pos.billingRule, numberLocale(), t('offers:billing.inArrearsShort'));

  return (
    <Card
      title={t('offers:positions.title')}
      actions={
        isEditable && hasObject ? (
          <Button
            onClick={onAdd}
            size="sm"
            leftIcon={<Plus className="w-3.5 h-3.5" strokeWidth={2} />}
          >
            {t('offers:positions.add')}
          </Button>
        ) : undefined
      }
      noPadding={positions.length > 0}
    >
      {reviewCount > 0 && (
        // why: positions without billing kind block the monthly run for the object (FAFD-27).
        <div className="mx-4 my-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-900">
          {t('offers:billing.reviewHint', { count: reviewCount })}
        </div>
      )}
      {isEditable && !hasObject && (
        <div className="mx-4 my-3 flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
          <Info className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" strokeWidth={2} />
          <p className="text-[12px] text-slate-600">{t('offers:positions.objectRequired.hint')}</p>
        </div>
      )}
      <NoTaskHint positions={positions} className="mx-4 my-3" />
      {positions.length === 0 ? (
        <EmptyState
          title={t('offers:positions.empty')}
          description={t('offers:positions.emptyDescription')}
        />
      ) : (
        <Table>
          <TableHead>
            <TableRow>
              <TableHeader className="w-16">
                {t('offers:positions.fields.positionNumber')}
              </TableHeader>
              <TableHeader>
                {t('offers:positions.fields.title')}
              </TableHeader>
              <TableHeader className="hidden xl:table-cell">
                {t('offers:positions.fields.description')}
              </TableHeader>
              <TableHeader className="hidden lg:table-cell">
                {t('offers:positions.fields.interval')}
              </TableHeader>
              <TableHeader>
                {t('offers:billing.label')}
              </TableHeader>
              <TableHeader className="text-right">
                {t('offers:positions.fields.netAmount')}
              </TableHeader>
              {isEditable && (
                <TableHeader className="w-32 text-right">
                  {t('offers:table.actions')}
                </TableHeader>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {positions.map((pos, idx) => {
              const hasInstructions = !!pos.instructions;
              const isExpanded = expandedIds.has(
                pos.id ?? ''
              );

              return (
                <Fragment key={pos.id}>
                  <TableRow>
                    <TableCell className="tabular-nums text-slate-500">
                      {pos.positionNumber ?? idx + 1}
                    </TableCell>
                    <TableCell className="text-[13px] font-medium text-slate-900">
                      <div className="flex items-center gap-2 flex-wrap">
                        {hasInstructions && (
                          <button
                            type="button"
                            onClick={() => toggleExpand(pos.id ?? '')}
                            aria-label={t('offers:positions.fields.instructions')}
                            className="text-slate-400 hover:text-slate-700 transition-colors"
                          >
                            {isExpanded
                              ? <ChevronUp className="w-4 h-4" strokeWidth={2} />
                              : <ChevronDown className="w-4 h-4" strokeWidth={2} />}
                          </button>
                        )}
                        {pos.taskDefinitionId ? (
                          // why: jump straight to the task the position bills (FAFD-27).
                          <Link
                            to={`/tasks/${pos.taskDefinitionId}`}
                            className="inline-flex items-center gap-1 hover:text-[#00A871] hover:underline"
                            title={t('offers:positions.openTask')}
                          >
                            {pos.title || '\u2014'}
                            <Link2 className="w-3.5 h-3.5 text-slate-400" strokeWidth={2} />
                          </Link>
                        ) : (
                          <>
                            <span>{pos.title || '\u2014'}</span>
                            <Badge
                              variant={pos.recurrenceKind === BillingKinds.ByEffort ? 'danger' : 'warning'}
                              title={t('offers:positions.noTaskTooltip')}
                            >
                              {t('offers:positions.noTask')}
                            </Badge>
                          </>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="hidden xl:table-cell text-slate-500">
                      {pos.description
                        ? pos.description.length > 50
                          ? `${pos.description.slice(0, 50)}\u2026`
                          : pos.description
                        : '\u2014'}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell text-slate-600">
                      {pos.interval || '\u2014'}
                    </TableCell>
                    <TableCell>
                      {needsReview(pos.recurrenceKind) ? (
                        <Badge variant="danger">{t('offers:billing.kinds.Unknown')}</Badge>
                      ) : (
                        <>
                          <div className="text-[13px] text-slate-700">{t(billingKindLabelKey(pos.recurrenceKind))}</div>
                          {ruleText(pos) && (
                            <div className="text-[11px] text-slate-500 tabular-nums">{ruleText(pos)}</div>
                          )}
                        </>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-medium text-slate-900 tabular-nums">
                      {formatMoney(pos.netAmount)}
                    </TableCell>
                    {isEditable && (
                      <TableCell>
                        <div className="flex items-center justify-end gap-1">
                          {idx > 0 && (
                            <Button
                              variant="ghost" size="sm"
                              title={t('common:actions.moveUp', { defaultValue: 'Nach oben' })}
                              aria-label={t('common:actions.moveUp', { defaultValue: 'Nach oben' })}
                              className="p-1.5"
                              onClick={() => onMove(idx, 'up')}
                            >
                              <ChevronUp className="w-4 h-4" strokeWidth={2} />
                            </Button>
                          )}
                          {idx < positions.length - 1 && (
                            <Button
                              variant="ghost" size="sm"
                              title={t('common:actions.moveDown', { defaultValue: 'Nach unten' })}
                              aria-label={t('common:actions.moveDown', { defaultValue: 'Nach unten' })}
                              className="p-1.5"
                              onClick={() => onMove(idx, 'down')}
                            >
                              <ChevronDown className="w-4 h-4" strokeWidth={2} />
                            </Button>
                          )}
                          <Button
                            variant="ghost" size="sm"
                            title={t('common:actions.edit')}
                            aria-label={t('common:actions.edit')}
                            className="p-1.5"
                            onClick={() => onEdit(pos)}
                          >
                            <Pencil className="w-4 h-4" strokeWidth={2} />
                          </Button>
                          <Button
                            variant="ghost" size="sm"
                            title={t('common:actions.delete')}
                            aria-label={t('common:actions.delete')}
                            className="p-1.5"
                            onClick={() => onDelete(pos)}
                          >
                            <Trash2 className="w-4 h-4 text-red-600" strokeWidth={2} />
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                  {isExpanded && pos.instructions && (
                    <tr key={`${pos.id}-instructions`}>
                      <td
                        colSpan={colCount}
                        className="px-4 py-3 bg-slate-50"
                      >
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
                          {t('offers:positions.fields.instructions')}
                        </p>
                        <div
                          className="text-[13px] text-slate-700 prose prose-sm max-w-none"
                          dangerouslySetInnerHTML={{
                            __html: pos.instructions,
                          }}
                        />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      )}
    </Card>
  );
}
